import { Popover } from '@systemeio/components';
import debounce from 'lodash.debounce';
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { HexAlphaColorPicker, HexColorInput, HexColorPicker } from 'react-colorful';
interface ColorPickerProps {
  ariaLabel: string;
  color: string;
  onChange: (color: string) => void;
  /** Custom trigger content. Defaults to a round swatch of the current color. */
  children?: ReactNode;
  showAlpha?: boolean;
}
function ColorPicker({
  ariaLabel,
  children,
  color,
  onChange,
  showAlpha = true
}: ColorPickerProps) {
  const [draft, setDraft] = useState(color);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const commit = useMemo(() => debounce((value: string) => onChangeRef.current(value), 300), []);
  useEffect(() => () => commit.cancel(), [commit]);
  useEffect(() => {
    commit.cancel();
    setDraft(color);
  }, [color, commit]);
  const changeColor = (value_0: string) => {
    setDraft(value_0);
    commit(value_0);
  };
  const Picker = showAlpha ? HexAlphaColorPicker : HexColorPicker;
  return <Popover align="end" side="bottom" showArrow={false} rootProps={{
    onOpenChange: open => {
      if (!open) commit.flush();
    }
  }} className={children ? 'w-full' : 'rounded-full'} popoverClassName="p-3" triggerProps={{
    'aria-label': ariaLabel
  }} label={<div className="flex w-50 flex-col gap-3">
          <Picker color={draft} onChange={changeColor} />
          <div className="border-field-border flex h-8 overflow-hidden rounded-md border">
            <span className="bg-bg-secondary text-text-secondary flex items-center px-2">#</span>
            <HexColorInput alpha={showAlpha} aria-label={ariaLabel} className="text-text-primary min-w-0 flex-1 px-2 outline-none" color={draft} onChange={changeColor} />
          </div>
        </div>}>
      {children ?? <span aria-hidden className="border-border-200 block size-5 rounded-full border" style={{
      backgroundColor: draft
    }} />}
    </Popover>;
}
export { ColorPicker };
export type { ColorPickerProps };